import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Checkbox,
  FormRow,
  Input,
  Modal,
  Stack,
  Text
} from '@efast_public/fjd-component-library';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { createJoiWithTranslations } from '../utils/joiConfig';
import { useNotification } from '../shared/notification/useNotification';
import { GovosApiRoutes, govosClient } from '../api/govos-api';
import { removeEmptyValues } from '../utils/objectUtils';
import { Partner } from '../api/govos.types';

type KontaktinformationenFormData = Pick<
  Partner,
  | 'dispname'
  | 'email'
  | 'emailHide'
  | 'orgname1'
  | 'orgname2'
  | 'ars'
  | 'street'
  | 'plz'
  | 'ort'
  | 'phone'
  | 'contacturl'
>;

interface EditKontaktinformationenModalProps {
  onClose?: () => void;
  onSuccess?: () => void;
  isOpen: boolean;
  zustaendigeStelle: Partner;
}

const getDefaultValues = (data?: Partner): KontaktinformationenFormData => ({
  dispname: data?.dispname || '',
  email: data?.email || '',
  emailHide: data?.emailHide || false,
  orgname1: data?.orgname1 || '',
  orgname2: data?.orgname2 || '',
  ars: data?.ars || '',
  street: data?.street || '',
  plz: data?.plz || '',
  ort: data?.ort || '',
  phone: data?.phone || '',
  contacturl: data?.contacturl || ''
});

const getValidationSchema = (
  CustomJoi: ReturnType<typeof createJoiWithTranslations>
) =>
  CustomJoi.object<KontaktinformationenFormData>({
    dispname: CustomJoi.string().required().messages({
      'string.empty': 'Bitte geben Sie eine Bezeichnung an',
      'any.required': 'Bitte geben Sie eine Bezeichnung an'
    }),
    email: CustomJoi.string()
      .email({ tlds: { allow: false } })
      .required(),
    emailHide: CustomJoi.boolean().optional(),
    orgname1: CustomJoi.string().required().messages({
      'string.empty':
        'Bitte geben Sie den Namen der Organisation oder Behörde an',
      'any.required':
        'Bitte geben Sie den Namen der Organisation oder Behörde an'
    }),
    orgname2: CustomJoi.string().optional().allow(''),
    ars: CustomJoi.string()
      .optional()
      .allow('')
      .pattern(/^\d{12}$/)
      .messages({
        'string.pattern.base':
          'Bitte geben Sie einen gültigen 12 stelligen Amtlichen Regionalschlüssel bestehend aus Ziffern ein'
      }),
    street: CustomJoi.string().optional().allow(''),
    plz: CustomJoi.string()
      .optional()
      .allow('')
      .pattern(/^\d{5}$/)
      .messages({
        'string.pattern.base':
          'Bitte geben Sie eine gültige 5 stellige Postleitzahl bestehend aus Ziffern ein'
      }),
    ort: CustomJoi.string().optional().allow(''),
    phone: CustomJoi.string().optional().allow(''),
    contacturl: CustomJoi.string().optional().allow('').uri().messages({
      'string.uri': 'Bitte geben Sie einen vollständigen Link an'
    })
  });

export function EditKontaktinformationenModal({
  onClose,
  onSuccess,
  isOpen,
  zustaendigeStelle
}: EditKontaktinformationenModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { notifyUserFromRequest } = useNotification();
  const CustomJoi = createJoiWithTranslations(t);

  const { register, handleSubmit, formState, control, trigger } =
    useForm<KontaktinformationenFormData>({
      defaultValues: getDefaultValues(zustaendigeStelle),
      resolver: joiResolver(getValidationSchema(CustomJoi)),
      mode: 'onBlur'
    });

  // Trigger validation on mount to show errors for invalid fields
  useEffect(() => {
    if (isOpen) {
      trigger();
    }
  }, [isOpen, trigger]);

  const { mutate: saveKontaktinformationen, isPending } = useMutation({
    mutationFn: async (data: KontaktinformationenFormData) => {
      return notifyUserFromRequest(
        () =>
          govosClient.put(
            GovosApiRoutes.zustaendigeStelle(zustaendigeStelle?.appiid),
            removeEmptyValues(data)
          ),
        {
          successMessage: t('EditKontaktinformationenModal.successMessage'),
          errorMessage: t('EditKontaktinformationenModal.errorMessage'),
          loadingMessage: t('EditKontaktinformationenModal.loadingMessage')
        }
      );
    },
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: [GovosApiRoutes.zustaendigeStelle(zustaendigeStelle.appiid)]
      });
      onSuccess?.();
    }
  });

  const handleSave = handleSubmit(async (data) => {
    saveKontaktinformationen(data);
  });

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Modal.Header id="modal-header">
        <Text size="xl" strong>
          {t('EditKontaktinformationenModal.title')}
        </Text>
      </Modal.Header>
      <Modal.Content>
        <Stack spacing="l">
          <Alert
            variant="info"
            message={t('EditKontaktinformationenModal.alertMessage')}
          />

          <Stack spacing="xs">
            <FormRow
              label={t('ZustaendigeStelleEditModal.fields.bezeichnung')}
              tooltip={t('ZustaendigeStelleEditModal.fields.bezeichnungHint')}
              validationMessage={formState.errors.dispname?.message}
              variant={formState.errors.dispname ? 'error' : undefined}
            >
              <Input
                {...register('dispname')}
                placeholder={t(
                  'ZustaendigeStelleEditModal.fields.bezeichnungPlaceholder'
                )}
              />
            </FormRow>

            <FormRow
              label={t('ZustaendigeStelleEditModal.fields.emailAddresses')}
              tooltip={t(
                'ZustaendigeStelleEditModal.fields.emailAddressesHint'
              )}
              validationMessage={formState.errors.email?.message}
              variant={formState.errors.email ? 'error' : undefined}
            >
              <Input
                {...register('email')}
                placeholder={t(
                  'ZustaendigeStelleEditModal.fields.emailPlaceholder'
                )}
              />
            </FormRow>

            <FormRow>
              <Controller
                name="emailHide"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    checked={field.value}
                    onChange={(checked) => field.onChange(checked)}
                    label={t(
                      'ZustaendigeStelleEditModal.fields.hideEmailFromApplicants'
                    )}
                  />
                )}
              />
            </FormRow>

            <FormRow
              label={t('ZustaendigeStelleEditModal.fields.organisationName')}
              tooltip={t(
                'ZustaendigeStelleEditModal.fields.organisationNameHint'
              )}
              validationMessage={formState.errors.orgname1?.message}
              variant={formState.errors.orgname1 ? 'error' : undefined}
            >
              <Input
                {...register('orgname1')}
                placeholder={t(
                  'ZustaendigeStelleEditModal.fields.organisationNamePlaceholder'
                )}
              />
            </FormRow>

            <FormRow
              label={t('ZustaendigeStelleEditModal.fields.abteilung')}
              validationMessage={formState.errors.orgname2?.message}
            >
              <Input
                {...register('orgname2')}
                placeholder={t(
                  'ZustaendigeStelleEditModal.fields.abteilungPlaceholder'
                )}
              />
            </FormRow>

            <FormRow
              label={t('ZustaendigeStelleEditModal.fields.ars')}
              tooltip={t('ZustaendigeStelleEditModal.fields.arsHint')}
              validationMessage={formState.errors.ars?.message}
              variant={formState.errors.ars ? 'error' : undefined}
            >
              <Input
                {...register('ars')}
                placeholder={t(
                  'ZustaendigeStelleEditModal.fields.arsPlaceholder'
                )}
                maxLength={12}
              />
            </FormRow>

            <Text size="s" style={{ marginTop: '16px' }}>
              {t('ZustaendigeStelleEditModal.postalContactNote')}
            </Text>

            <FormRow
              label={t('ZustaendigeStelleEditModal.fields.strasse')}
              validationMessage={formState.errors.street?.message}
            >
              <Input
                {...register('street')}
                placeholder={t(
                  'ZustaendigeStelleEditModal.fields.strassePlaceholder'
                )}
              />
            </FormRow>

            <FormRow
              label={t('ZustaendigeStelleEditModal.fields.postleitzahl')}
              validationMessage={formState.errors.plz?.message}
              variant={formState.errors.plz ? 'error' : undefined}
            >
              <Input
                {...register('plz')}
                placeholder={t(
                  'ZustaendigeStelleEditModal.fields.postleitzahlPlaceholder'
                )}
                maxLength={5}
              />
            </FormRow>

            <FormRow
              label={t('ZustaendigeStelleEditModal.fields.ort')}
              validationMessage={formState.errors.ort?.message}
            >
              <Input
                {...register('ort')}
                placeholder={t(
                  'ZustaendigeStelleEditModal.fields.ortPlaceholder'
                )}
              />
            </FormRow>

            <FormRow
              label={t('ZustaendigeStelleEditModal.fields.telefon')}
              validationMessage={formState.errors.phone?.message}
            >
              <Input
                {...register('phone')}
                placeholder={t(
                  'ZustaendigeStelleEditModal.fields.telefonPlaceholder'
                )}
              />
            </FormRow>

            <FormRow
              label={t('ZustaendigeStelleEditModal.fields.kontaktseiteUrl')}
              tooltip={t(
                'ZustaendigeStelleEditModal.fields.kontaktseiteUrlHint'
              )}
              validationMessage={formState.errors.contacturl?.message}
              variant={formState.errors.contacturl ? 'error' : undefined}
            >
              <Input
                {...register('contacturl')}
                placeholder={t(
                  'ZustaendigeStelleEditModal.fields.kontaktseiteUrlPlaceholder'
                )}
              />
            </FormRow>
          </Stack>
        </Stack>
      </Modal.Content>
      <Modal.Footer>
        <div style={{ display: 'flex', gap: '12px', width: '100%' }}>
          <Button
            variant="secondary-outline"
            onClick={onClose}
            disabled={isPending}
            style={{ flex: 1 }}
          >
            {t('ZustaendigeStelleEditModal.actions.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleSave()}
            disabled={!formState.isValid || isPending}
            style={{ flex: 1 }}
          >
            {t('ZustaendigeStelleEditModal.actions.save')}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
